import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  doc, docData, updateDoc,
  collection, collectionData,
  query, orderBy, where,
  serverTimestamp,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Auth } from '@angular/fire/auth';
import { Observable, switchMap, of } from 'rxjs';
import { Partner, PartnerStatus } from '../models/partner.model';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class PartnerService {
  private fs    = inject(Firestore);
  private fns   = inject(Functions);
  private auth  = inject(AuthService);

  /** Stream current partner's own document */
  currentPartner$(): Observable<Partner | undefined> {
    return this.auth.user$.pipe(
      switchMap(user => {
        if (!user) return of(undefined);
        return docData(doc(this.fs, `Partners/${user.uid}`), { idField: 'uid' }) as Observable<Partner>;
      })
    );
  }

  /** Stream a specific partner by uid (admin use) */
  partner$(uid: string): Observable<Partner | undefined> {
    return docData(doc(this.fs, `Partners/${uid}`), { idField: 'uid' }) as Observable<Partner>;
  }

  /** Stream all partners ordered by createdAt desc (admin use) */
  allPartners$(): Observable<Partner[]> {
    const ref = collection(this.fs, 'Partners');
    return collectionData(query(ref, orderBy('createdAt', 'desc')), { idField: 'uid' }) as Observable<Partner[]>;
  }

  /** Stream partners by status (admin use) */
  partnersByStatus$(status: PartnerStatus): Observable<Partner[]> {
    const ref = collection(this.fs, 'Partners');
    return collectionData(
      query(ref, where('status', '==', status), orderBy('createdAt', 'desc')),
      { idField: 'uid' }
    ) as Observable<Partner[]>;
  }

  /** Update partner profile — direct Firestore write (own doc only) */
  async updateProfile(data: Partial<Partner>): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    const updates: Record<string, any> = {};
    if (data.displayName !== undefined) updates['displayName'] = data.displayName;
    if (data.companyName !== undefined) updates['companyName'] = data.companyName;
    if (data.phone !== undefined) {
      if (data.phone && !/^\d{10}$/.test(String(data.phone))) {
        throw new Error('Phone must be exactly 10 digits.');
      }
      updates['phone'] = data.phone;
    }
    if (data.address !== undefined) updates['address'] = data.address;
    if (data.website !== undefined) updates['website'] = data.website;
    updates['updatedAt'] = new Date();

    await updateDoc(doc(this.fs, `Partners/${user.uid}`), updates);
  }

  /** Admin: approve, suspend, or terminate a partner — direct write (admin rule-gated) */
  async setStatus(partnerUid: string, status: PartnerStatus, reason?: string): Promise<void> {
    const currentUser = this.auth.currentUser;
    if (!currentUser) throw new Error('Not authenticated');
    const updates: Record<string, any> = { status, updatedAt: serverTimestamp() };
    if (status === 'active') {
      updates['approvedAt'] = serverTimestamp();
      updates['approvedBy'] = currentUser.uid;
    }
    if (reason) updates['suspendedReason'] = reason;
    await updateDoc(doc(this.fs, `Partners/${partnerUid}`), updates);
  }

  /** Admin: set commission rate — direct write (admin rule-gated) */
  async setCommissionRate(partnerUid: string, commissionRate: number): Promise<void> {
    if (commissionRate < 0 || commissionRate > 100) {
      throw new Error('commissionRate must be between 0 and 100.');
    }
    await updateDoc(doc(this.fs, `Partners/${partnerUid}`), {
      commissionRate,
      updatedAt: serverTimestamp(),
    });
  }

  /** Create Stripe Express account for payouts */
  async createStripeAccount(): Promise<{ accountId: string }> {
    const fn = httpsCallable(this.fns, 'managePartner');
    const result = await fn({ action: 'createStripeAccount' });
    return result.data as { accountId: string };
  }

  /** Get Stripe onboarding link for Express account setup */
  async getStripeOnboardingLink(returnUrl: string, refreshUrl: string): Promise<{ url: string }> {
    const fn = httpsCallable(this.fns, 'managePartner');
    const result = await fn({ action: 'getStripeOnboardingLink', returnUrl, refreshUrl });
    return result.data as { url: string };
  }

  /** Check Stripe Express account status */
  async checkStripeAccountStatus(): Promise<{ connected: boolean; payoutsEnabled: boolean; detailsSubmitted: boolean }> {
    const fn = httpsCallable(this.fns, 'managePartner');
    const result = await fn({ action: 'checkStripeAccountStatus' });
    return result.data as any;
  }

  /** Dashboard aggregates (stays as CF — cross-collection aggregation) */
  async getDashboard(): Promise<unknown> {
    const fn = httpsCallable(this.fns, 'getPartnerDashboard');
    const result = await fn({});
    return result.data;
  }

  /** Get partner's monthly analytics and revenue breakdown */
  async getMonthlyAnalytics(month?: string): Promise<unknown> {
    const fn = httpsCallable(this.fns, 'getPartnerMonthlyAnalytics');
    const result = await fn({month});
    return result.data;
  }
}
